/**
 * Opus AI Assistant - Zustand Store
 * State management for the AI assistant panel
 */

import { create } from 'zustand';

// =============================================================================
// TYPES
// =============================================================================

export interface OpusToolCall {
  name: string;
  duration: number;
  success: boolean;
}

export interface OpusMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: OpusToolCall[];
  createdAt: Date;
  isStreaming?: boolean;
}

export interface OpusConversation {
  id: string;
  title: string;
  messageCount: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface OpusUsage {
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

// =============================================================================
// STORE STATE
// =============================================================================

interface OpusAIState {
  // Panel state
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  togglePanel: () => void;

  // Conversations
  conversations: OpusConversation[];
  setConversations: (conversations: OpusConversation[]) => void;

  // Active conversation
  activeConversationId: string | null;
  setActiveConversationId: (id: string | null) => void;

  // Messages
  messages: OpusMessage[];
  setMessages: (messages: OpusMessage[]) => void;
  addMessage: (message: OpusMessage) => void;
  updateLastMessage: (content: string, toolCalls?: OpusToolCall[]) => void;

  // Loading state
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;

  // Streaming state
  streamingMessage: string;
  setStreamingMessage: (message: string) => void;
  appendStreamingMessage: (text: string) => void;

  // Tool activity
  activeTools: Array<{ name: string; startTime: number }>;
  addActiveTool: (name: string) => void;
  removeActiveTool: (name: string) => void;
  clearActiveTools: () => void;

  // Last usage
  lastUsage: OpusUsage | null;
  setLastUsage: (usage: OpusUsage | null) => void;

  // Error state
  error: string | null;
  setError: (error: string | null) => void;

  // Reset
  reset: () => void;
  startNewConversation: () => void;
}

// =============================================================================
// STORE IMPLEMENTATION
// =============================================================================

const initialState = {
  isOpen: false,
  conversations: [],
  activeConversationId: null,
  messages: [],
  isLoading: false,
  streamingMessage: '',
  activeTools: [],
  lastUsage: null,
  error: null,
};

export const useOpusAIStore = create<OpusAIState>((set) => ({
  ...initialState,

  // Panel controls
  setIsOpen: (open) => set({ isOpen: open }),
  togglePanel: () => set((state) => ({ isOpen: !state.isOpen })),

  // Conversations
  setConversations: (conversations) => set({ conversations }),

  // Active conversation
  setActiveConversationId: (id) => set({ activeConversationId: id }),

  // Messages
  setMessages: (messages) => set({ messages }),
  addMessage: (message) =>
    set((state) => ({
      messages: [...state.messages, message],
    })),
  updateLastMessage: (content, toolCalls) =>
    set((state) => {
      const messages = [...state.messages];
      const lastIndex = messages.length - 1;
      if (lastIndex >= 0 && messages[lastIndex].role === 'assistant') {
        messages[lastIndex] = {
          ...messages[lastIndex],
          content,
          toolCalls: toolCalls || messages[lastIndex].toolCalls,
          isStreaming: false,
        };
      }
      return { messages };
    }),

  // Loading
  setIsLoading: (loading) => set({ isLoading: loading }),

  // Streaming
  setStreamingMessage: (message) => set({ streamingMessage: message }),
  appendStreamingMessage: (text) =>
    set((state) => ({
      streamingMessage: state.streamingMessage + text,
    })),

  // Active tools
  addActiveTool: (name) =>
    set((state) => ({
      activeTools: [...state.activeTools, { name, startTime: Date.now() }],
    })),
  removeActiveTool: (name) =>
    set((state) => ({
      activeTools: state.activeTools.filter((t) => t.name !== name),
    })),
  clearActiveTools: () => set({ activeTools: [] }),

  // Usage
  setLastUsage: (usage) => set({ lastUsage: usage }),

  // Error
  setError: (error) => set({ error }),

  // Reset
  reset: () => set(initialState),
  startNewConversation: () =>
    set({
      activeConversationId: null,
      messages: [],
      streamingMessage: '',
      activeTools: [],
      error: null,
    }),
}));

// =============================================================================
// SELECTORS
// =============================================================================

export const selectIsOpen = (state: OpusAIState) => state.isOpen;
export const selectMessages = (state: OpusAIState) => state.messages;
export const selectIsLoading = (state: OpusAIState) => state.isLoading;
export const selectActiveTools = (state: OpusAIState) => state.activeTools;
export const selectError = (state: OpusAIState) => state.error;
export const selectActiveConversationId = (state: OpusAIState) => state.activeConversationId;
export const selectConversations = (state: OpusAIState) => state.conversations;
